import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { getProperty } from '../api';
import ListingImage from './ListingImage';

// admin_real_estate_property_management.md - the website owner opens a listing's full details in a
// popup (same "Details" pattern the other verticals' admin listings table uses via
// ProductDetailsModal). Read-only; editing is the separate "Edit" action on the row.
const STATUS_VARIANT = { available: 'success', booked: 'info', maintenance: 'warning', disabled: 'secondary' };

function siteAddressLine(a = {}) {
  return [a.siteAddressLine1, a.siteAddressLine2, a.siteArea, a.siteCity, a.siteState, a.sitePincode]
    .filter(Boolean)
    .join(', ');
}

export default function PropertyDetailsModal({ propertyId, show, onHide }) {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !propertyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setProperty(null);
    getProperty(propertyId)
      .then((data) => { if (!cancelled) setProperty(data); })
      .catch(() => { if (!cancelled) setError('Could not load this property.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, propertyId]);

  const a = property?.attributes || {};
  const amenities = Array.isArray(a.amenities) ? a.amenities : [];
  const address = siteAddressLine(a);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{property ? property.name : 'Property details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" role="status" />}
        {error && <Alert variant="danger">{error}</Alert>}
        {property && (
          <>
            <p className="mb-2">
              <Badge bg={STATUS_VARIANT[property.status] || 'secondary'}>{property.status}</Badge>
              {property.category && <span className="ms-2 text-muted">{property.category}</span>}
            </p>
            <p className="mb-1">
              {property.location} &middot; <strong>₹{property.price}</strong>/{property.priceUnit}
            </p>
            <p className="mb-2 text-muted small">
              {[
                a.bedrooms != null && `${a.bedrooms} bd`,
                a.bathrooms != null && `${a.bathrooms} ba`,
                a.areaSqft != null && `${a.areaSqft} sqft`
              ].filter(Boolean).join(' · ') || '—'}
            </p>

            {(property.ownerName || property.ownerEmail || property.ownerPhone || a.ownerPhone) && (
              <p className="mb-2">
                Owner: {property.ownerName || '—'}
                {(property.ownerEmail || property.ownerPhone || a.ownerPhone) && (
                  <span className="text-muted">
                    {' '}&middot; {[property.ownerEmail, property.ownerPhone || a.ownerPhone].filter(Boolean).join(' · ')}
                  </span>
                )}
              </p>
            )}

            {address && <p className="mb-2">Site address: {address}</p>}

            {property.description && <p className="mb-2">{property.description}</p>}

            {amenities.length > 0 && (
              <p className="mb-2">
                {amenities.map((am) => <Badge key={am} bg="light" text="dark" className="me-1 border">{am}</Badge>)}
              </p>
            )}

            {property.specifications?.length > 0 && (
              <ul className="small mb-2">
                {property.specifications.map((s, i) => (
                  <li key={`${s.label}-${i}`}><strong>{s.label}:</strong> {s.value}</li>
                ))}
              </ul>
            )}

            {property.images?.length > 0 && (
              <div className="d-flex flex-wrap gap-2">
                {property.images.map((src, i) => (
                  <ListingImage
                    key={src}
                    src={src}
                    alt={`${property.name} ${i + 1}`}
                    sizes="90px"
                    style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6 }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
